import { BsStars, BsTelephoneFill } from 'react-icons/bs';

/**
 * Add-on UI/config registry. Each entry drives the generic add-on components
 * (banner, gate modal, upsell panel, settings card) so a new add-on = a config
 * entry, not new components.
 *
 * @typedef {Object} AddonConfig
 * @property {string} key            - addonKey (matches backend orgAddons.addonKey, capability `<key>.access`)
 * @property {string} name           - display name (e.g. 'CRM')
 * @property {string} route          - where to send the user once they have access (e.g. '/crm/pipeline')
 * @property {string} priceLabel     - human price (e.g. '35 € / Monat (netto)')
 * @property {string} priceShort     - compact price for inline copy (e.g. '35 €/Monat')
 * @property {number} trialDays      - 0 = paid from day one (no trial state anywhere in the UI)
 * @property {boolean} proComp       - included in the pro plan (pro never sees an activate CTA)
 * @property {import('react').ElementType} icon
 * @property {string[]} benefits     - selling points shown as a checklist in upsell surfaces
 */

/** @type {AddonConfig} */
export const CRM_ADDON = {
	key: 'crm',
	name: 'CRM',
	route: '/crm/pipeline',
	priceLabel: '35 € / Monat (netto)',
	priceShort: '35 €/Monat',
	trialDays: 14,
	proComp: true,
	icon: BsStars,
	benefits: [
		'Pipeline mit Drag & Drop',
		'Firmen, Kontakte und Aktivitäten zentral',
		'Leads mit einem Klick übernehmen',
		'Deal-Historie mit Verweildauer je Phase',
	],
};

/**
 * Reveals vendor phone numbers (mobile, direct dial, switchboard) of enriched contacts.
 * @type {AddonConfig}
 */
export const PHONE_ADDON = {
	key: 'phone',
	name: 'Telefonnummern',
	route: '/leads',
	priceLabel: '49 € / Monat (netto)',
	priceShort: '49 €/Monat',
	trialDays: 0,
	proComp: false,
	icon: BsTelephoneFill,
	benefits: [
		'Mobilnummern und Durchwahlen der Entscheider, nicht nur die Zentrale',
		'Erreichbarkeit per Netzabfrage geprüft, tote Nummern werden aussortiert',
		'Sofort für alle bereits angereicherten Ansprechpartner freigeschaltet',
	],
};

/** All known add-on configs keyed by addonKey. */
export const ADDON_CONFIGS = {
	[CRM_ADDON.key]: CRM_ADDON,
	[PHONE_ADDON.key]: PHONE_ADDON,
};

const PLANS_WITHOUT_ADDON_MANAGEMENT = new Set(['demo', 'cancelled', 'paused']);

/**
 * Whether billing settings show the add-on's management card. Demo, cancelled and paused orgs
 * upgrade or resume via the plan cards; pro never buys an add-on its plan already includes.
 *
 * @param {AddonConfig} addon
 * @param {string|null} orgPlan
 * @returns {boolean}
 */
export function isAddonManageable(addon, orgPlan) {
	if (!orgPlan || PLANS_WITHOUT_ADDON_MANAGEMENT.has(orgPlan)) return false;
	return !(addon.proComp && orgPlan === 'pro');
}
